// components/BondForm.tsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  InputAdornment,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as BondIcon
} from '@mui/icons-material';
import {
  BondForm,
  BondExpandedSections,
  BondSectionKey,
  BondCompactTextFieldProps,
  BondSectionHeaderProps
} from '../simulationTypes';

interface BondFormComponentProps {
  form: BondForm;
  handleInputChange: (field: keyof BondForm, value: number | string) => void;
  expandedSections: BondExpandedSections;
  toggleSection: (section: BondSectionKey) => void;
  runBondSimulation: () => void;
  loading: boolean;
  error: string | null;
}

const CompactTextField: React.FC<
  BondCompactTextFieldProps & { form: BondForm; handleInputChange: any }
> = ({
  label,
  field,
  type = 'number',
  prefix = '',
  suffix = '',
  helperText = '',
  form,
  handleInputChange
}) => (
  <TextField
    label={label}
    type={type}
    size="small"
    fullWidth
    value={type === 'number' && suffix === '%' ? (form[field] as number) * 100 : form[field]}
    onChange={(e) => {
      const value =
        type === 'number'
          ? suffix === '%'
            ? parseFloat(e.target.value) / 100
            : parseFloat(e.target.value)
          : e.target.value;
      handleInputChange(field, value);
    }}
    InputProps={{
      startAdornment: prefix ? (
        <InputAdornment position="start">{prefix}</InputAdornment>
      ) : undefined,
      endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined
    }}
    helperText={helperText}
    sx={{ mb: 1 }}
  />
);

const SectionHeader: React.FC<
  BondSectionHeaderProps & { expandedSections: BondExpandedSections; toggleSection: any }
> = ({ title, section, icon, expandedSections, toggleSection }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      p: 1,
      borderRadius: 1,
      bgcolor: expandedSections[section] ? '#e3f2fd' : 'transparent',
      '&:hover': { bgcolor: '#f5f5f5' }
    }}
    onClick={() => toggleSection(section)}
  >
    {icon}
    <Typography variant="subtitle2" fontWeight="600" sx={{ ml: 1, flex: 1 }}>
      {title}
    </Typography>
    <IconButton size="small">
      {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </IconButton>
  </Box>
);

const BondFormComponent: React.FC<BondFormComponentProps> = ({
  form,
  handleInputChange,
  expandedSections,
  toggleSection,
  runBondSimulation,
  loading,
  error
}) => {
  return (
    <Card elevation={2} sx={{ height: 'fit-content', position: 'sticky', top: 16 }}>
              <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BondIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold" color="primary">
            Bond Setup
          </Typography>
        </Box>

        {/* Basic Config */}
        <SectionHeader
          title="Basic Config"
          section="basic"
          icon={<BondIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.basic}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Iterations"
                  field="iterations"
                  helperText="Monte Carlo runs"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Years"
                  field="years"
                  helperText="Investment period"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <CompactTextField
                  label="Bond Fund Name"
                  field="assetName"
                  type="text"
                  helperText="Government or corporate bond"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Investment Amount */}
        <SectionHeader
          title="Investment Amount"
          section="investment"
          icon={<BondIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.investment}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Purchase Price"
                  field="purchasePrice"
                  prefix="$"
                  helperText="Bond investment"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Fees"
                  field="fees"
                  prefix="$"
                  helperText="Transaction fees"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Setup Costs"
                  field="setupCosts"
                  prefix="$"
                  helperText="Initial costs"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Management Fee"
                  field="managementFeePercent"
                  suffix="%"
                  helperText="Annual fee"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Interest Rates */}
        <SectionHeader
          title="Interest Rates"
          section="interest"
          icon={<BondIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.interest}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Interest Min"
                  field="interestRateMin"
                  suffix="%"
                  helperText="Min yield"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Interest Max"
                  field="interestRateMax"
                  suffix="%"
                  helperText="Max yield"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Transaction Cost"
                  field="transactionCostPercent"
                  suffix="%"
                  helperText="Per transaction"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Growth Parameters */}
        <SectionHeader
          title="Growth Parameters"
          section="growth"
          icon={<BondIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.growth}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Capital Growth"
                  field="capitalGrowthFixed"
                  suffix="%"
                  helperText="Fixed growth"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Volatility"
                  field="volatilityStdDev"
                  suffix="%"
                  helperText="Price volatility"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Recession %"
                  field="recessionProb"
                  suffix="%"
                  helperText="Probability"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Recession Impact"
                  field="recessionImpact"
                  suffix="%"
                  helperText="Market impact"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Risk Factors */}
        <SectionHeader
          title="Risk Factors"
          section="risk"
          icon={<BondIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.risk}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Default Risk"
                  field="defaultProbability"
                  suffix="%"
                  helperText="Issuer default"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Liquidity Discount"
                  field="liquidityDiscount"
                  suffix="%"
                  helperText="Sale discount"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={runBondSimulation}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <BondIcon />}
            fullWidth
            sx={{ fontWeight: 'bold' }}
          >
            {loading ? 'Running Simulation...' : 'Run Bond Simulation'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default BondFormComponent;
